/**
 * AdminHepPrograms — assign HEP videos to patients as a program.
 * Iter 68.
 *
 * Each program is a bundle of (video, sets, reps, hold_sec, rest_sec, notes)
 * for a specific patient. Patients see the program on /patient/hep and log
 * adherence with a "Did this" button per exercise.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Users, Plus, Trash2, Video, Activity, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

export default function AdminHepPrograms() {
  const [programs, setPrograms] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [adherence, setAdherence] = useState({});  // {[program_id]: count}

  const load = async () => {
    const r = await api.get('/hep/programs');
    setPrograms(r.data || []);
    // Load adherence counts for each program
    const counts = {};
    for (const p of (r.data || [])) {
      try {
        const a = await api.get('/hep/adherence', { params: { program_id: p.program_id } });
        counts[p.program_id] = (a.data || []).length;
      } catch {}
    }
    setAdherence(counts);
  };

  useEffect(() => {
    load();
    api.get('/patients').then(r => setPatients(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!await safeConfirm('Archive this program?')) return;
    await api.delete(`/hep/programs/${id}`);
    setPrograms(p => p.filter(x => x.program_id !== id));
    toast.success('Archived.');
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">HOME EXERCISE PROGRAMS</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Patient HEPs</h1>
          <p className="text-fg-muted mt-2 text-sm">Assign exercises to patients with sets, reps, rest, and notes. Track their daily adherence.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/hep" className="btn-secondary inline-flex items-center gap-2" data-testid="hep-library-link">
            <Video className="w-4 h-4"/> Video library
          </Link>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="hep-program-create-btn">
            <Plus className="w-4 h-4"/> Assign program
          </button>
        </div>
      </div>

      <div className="bento-card overflow-x-auto" data-testid="hep-programs-list">
        {programs.length === 0 ? (
          <div className="text-center py-8 text-sm text-fg-muted">No programs yet. Click "Assign program" to create the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-fg-muted text-xs uppercase tracking-wider border-b border-soft">
                <th className="text-left py-2 px-2">Patient</th>
                <th className="text-left py-2 px-2">Program</th>
                <th className="text-left py-2 px-2">Exercises</th>
                <th className="text-left py-2 px-2">Frequency</th>
                <th className="text-left py-2 px-2">Adherence</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.map(p => {
                const patient = patients.find(pt => pt.user_id === p.patient_id);
                return (
                  <tr key={p.program_id} className="border-b border-soft/50 hover:bg-bg-secondary/30">
                    <td className="py-3 px-2 font-medium">{patient?.full_name || patient?.name || '—'}</td>
                    <td className="py-3 px-2">{p.name}</td>
                    <td className="py-3 px-2">{(p.items || []).length}</td>
                    <td className="py-3 px-2 text-xs text-fg-muted">{p.frequency_per_week || 3}×/week</td>
                    <td className="py-3 px-2">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <Activity className="w-3 h-3 text-brand"/> {adherence[p.program_id] || 0} logs
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => del(p.program_id)} className="text-[#B83A3A] hover:opacity-70" data-testid={`hep-program-del-${p.program_id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <ProgramModal onClose={() => setShowCreate(false)} patients={patients} onSaved={() => { setShowCreate(false); load(); }}/>
      )}
    </div>
  );
}

function ProgramModal({ onClose, onSaved, patients }) {
  const [form, setForm] = useState({
    patient_id: '',
    name: '',
    frequency_per_week: 3,
    notes: '',
    items: [],
  });
  const [videos, setVideos] = useState([]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/hep/videos').then(r => setVideos(r.data || []));
  }, []);

  const addVideo = (v) => {
    setForm(f => ({
      ...f,
      items: [...f.items, { video_id: v.video_id, sets: 3, reps: 10, hold_sec: null, rest_sec: 60, notes: '' }],
    }));
    setPicking(false);
  };

  const updateItem = (idx, patch) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.patient_id) { toast.error('Pick a patient.'); return; }
    if (!form.name) { toast.error('Name the program.'); return; }
    if (form.items.length === 0) { toast.error('Add at least one exercise.'); return; }
    setSaving(true);
    try {
      await api.post('/hep/programs', form);
      toast.success('Program assigned.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-16 px-4" onClick={onClose}>
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="hep-program-modal">
        <div className="sticky top-0 bg-bg-primary border-b border-soft p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Assign HEP program</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Patient">
              <select className="input" value={form.patient_id}
                      onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}
                      data-testid="hep-program-patient">
                <option value="">— Select —</option>
                {patients.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name || p.name || p.email}</option>)}
              </select>
            </Field>
            <Field label="Frequency (per week)">
              <input type="number" min="1" max="14" className="input" value={form.frequency_per_week}
                     onChange={(e) => setForm(f => ({ ...f, frequency_per_week: parseInt(e.target.value) || 3 }))}
                     data-testid="hep-program-freq"/>
            </Field>
          </div>
          <Field label="Program name">
            <input className="input" placeholder='e.g. "Lower-back rehab week 1-2"' value={form.name}
                   onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                   data-testid="hep-program-name"/>
          </Field>

          <div className="border border-soft rounded-md p-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm inline-flex items-center gap-1"><Video className="w-3.5 h-3.5"/> Exercises ({form.items.length})</h4>
              <button type="button" onClick={() => setPicking(true)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="hep-program-add-video">
                <Plus className="w-3 h-3"/> Add video
              </button>
            </div>
            {form.items.length === 0 ? (
              <div className="text-xs text-fg-muted text-center py-4">No exercises yet — click "Add video" to start.</div>
            ) : (
              <div className="space-y-2">
                {form.items.map((it, idx) => {
                  const v = videos.find(x => x.video_id === it.video_id);
                  return (
                    <div key={idx} className="border border-soft rounded-md p-2.5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-sm font-medium flex-1 truncate">{v?.name || it.video_id}</div>
                        <button onClick={() => removeItem(idx)} className="text-[#B83A3A] hover:opacity-70 flex-shrink-0">
                          <X className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-1.5">
                        <NumberField label="Sets" v={it.sets} on={(v) => updateItem(idx, { sets: v })}/>
                        <NumberField label="Reps" v={it.reps} on={(v) => updateItem(idx, { reps: v })}/>
                        <NumberField label="Hold (s)" v={it.hold_sec || ''} on={(v) => updateItem(idx, { hold_sec: v })}/>
                        <NumberField label="Rest (s)" v={it.rest_sec} on={(v) => updateItem(idx, { rest_sec: v })}/>
                      </div>
                      <input className="input mt-2 !text-xs" placeholder="Notes for the patient (form cues, etc.)"
                             value={it.notes || ''}
                             onChange={(e) => updateItem(idx, { notes: e.target.value })}/>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Field label="Notes for patient">
            <textarea className="input min-h-[60px] !text-sm" value={form.notes}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
          </Field>
        </div>

        <div className="p-4 border-t border-soft flex justify-end gap-2 sticky bottom-0 bg-bg-primary">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="hep-program-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Check className="w-3 h-3"/>}
            {saving ? 'Saving…' : 'Assign program'}
          </button>
        </div>

        {picking && (
          <VideoPickerModal videos={videos} onPick={addVideo} onClose={() => setPicking(false)}/>
        )}
      </div>
    </div>
  );
}

function VideoPickerModal({ videos, onPick, onClose }) {
  const [q, setQ] = useState('');
  const filtered = videos.filter(v => !q || v.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start justify-center pt-20 px-4" onClick={onClose}>
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-lg max-h-[70vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-soft flex items-center justify-between">
          <h4 className="font-heading font-semibold text-sm">Pick a video</h4>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        <div className="p-3 border-b border-soft">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                 className="input !text-sm" autoFocus/>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {filtered.map(v => (
            <button key={v.video_id} onClick={() => onPick(v)}
                    className="w-full text-left p-2.5 rounded-md border border-soft hover:border-brand hover:bg-brand/5 transition flex items-center gap-2"
                    data-testid={`hep-pick-video-${v.video_id}`}>
              <Video className="w-4 h-4 text-brand flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{v.name}</div>
                <div className="text-[11px] text-fg-muted">{v.body_region || '—'} · {v.difficulty || 'beginner'}</div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-fg-muted text-center py-4">No videos. Add one in the library first.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, v, on }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-fg-muted">{label}</div>
      <input type="number" className="input !text-xs !py-1 !px-1.5" value={v ?? ''}
             onChange={(e) => on(e.target.value === '' ? null : parseInt(e.target.value))}/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
